import React, { Fragment, forwardRef, useImperativeHandle } from 'react'
import { Grid, Tooltip, CircularProgress } from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { Observer } from 'mobx-react'

import { useStyles } from './AlertSettings.styles'

import classNames from 'classnames'
import kebabCase from 'lodash/kebabCase'
import AppState from 'config/store/AppState'

import AlertType, { NotificationSettingsData } from './AlertType/AlertType'
import { alertTypeFrequencyOptions } from './ManageReceivers/ManageReceivers'
import {
  useReceiversNotificationSettingsQuery,
  useUpsertNotificationSettings,
} from './hooks/index'

import { useStores } from 'config/store'
interface AlertSettingsProps {
  ref: React.MutableRefObject<null>
  isPrimaryContact: boolean
}

// TODO: Add ALERT TYPE to the array to activate
const activeAlertSettings = [
  'Missed Call Alert',
  'New Appointment Call Alert',
  'New Opportunity Call Alert',
  'First Opportunity Alert',
  'En Route Alert',
]

function sortByAlertTypeAsc(
  a: NotificationSettingsData,
  b: NotificationSettingsData
) {
  return a.alertType.toLowerCase().localeCompare(b.alertType.toLowerCase())
}

const AlertSettings = forwardRef<{}, AlertSettingsProps>(
  ({ isPrimaryContact }, ref) => {
    const { notificationSettingsStore } = useStores()
    const classes = useStyles()
    useImperativeHandle(ref, () => {
      return {
        saveNotificationSettings: saveNotificationSettings,
        cancelNotificationSettings: cancelNotificationSettings,
      }
    })

    const getFilteredUserSettings = (
      _map: Map<string, NotificationSettingsData>
    ) => {
      return new Map(
        Array.from(_map).filter(([_key, value]) => {
          // TODO: Remove the second condition if all alert types are activated
          return (
            value.contactId === AppState.contactId &&
            activeAlertSettings.includes(value.alertType)
          )
        })
      )
    }

    const {
      data: receiversData = new Map(),
      status: receiversStatus,
      refetch,
      isFetching,
      isRefetchError,
    } = useReceiversNotificationSettingsQuery()

    const { mutateAsync } = useUpsertNotificationSettings()

    notificationSettingsStore.initializeSettingsData(
      new Map([...receiversData])
    )

    const saveNotificationSettings = async () => {
      try {
        const result = await mutateAsync(
          notificationSettingsStore.updatedSettings
        )
        notificationSettingsStore.clear()
        return result
      } catch (error) {
        console.error(error)
      }
    }

    const cancelNotificationSettings = () => {
      notificationSettingsStore.clear()
      notificationSettingsStore.initializeSettingsData(
        new Map([...receiversData])
      )
    }

    const onFrequencyChange = (
      value: NotificationSettingsData,
      frequency: string
    ) => {
      const key = kebabCase(value.alertType + '-' + value.contactId)
      const _value = { ...value, frequency: frequency }
      notificationSettingsStore.setSettingsData(key, _value)
    }

    const onEmailChange = (
      value: NotificationSettingsData,
      checked: boolean
    ) => {
      const key = kebabCase(value.alertType + '-' + value.contactId)
      const _value = { ...value, email: checked }
      notificationSettingsStore.setSettingsData(key, _value)
    }

    const onSmsChange = (value: NotificationSettingsData, checked: boolean) => {
      const key = kebabCase(value.alertType + '-' + value.contactId)
      const _value = { ...value, sms: checked }
      notificationSettingsStore.setSettingsData(key, _value)
    }

    if (receiversStatus === 'loading' || isFetching) {
      return (
        <div>
          <CircularProgress size={14} className={classes.spinner} />
        </div>
      )
    }

    if (receiversStatus === 'error') {
      return <div>Error fetching data</div>
    }

    return (
      <Observer
        render={() => {
          if (notificationSettingsStore.shouldRefetch) {
            refetch()
            notificationSettingsStore.setShouldRefetch(false)
          }

          const userSettings = getFilteredUserSettings(
            notificationSettingsStore.settingsData
          )

          return (
            <Fragment>
              <Grid item xs={7} style={{ marginTop: '40px' }}>
                <div className={classes.row}>
                  <div className={classes.col}>
                    <h4 className={classes.heading}>Notifications</h4>
                  </div>
                </div>
              </Grid>
              <hr className={classes.separator} />
              <div className={classNames(classes.row, classes.rowHeading)}>
                <div className={classes.col}></div>
                <div className={classes.col}>
                  <label>Receive alert from</label>
                </div>
                <div className={classes.col}>
                  <label>Frequency</label>
                </div>
                {isPrimaryContact && (
                  <div className={classes.col}>
                    <label>
                      Alert Receivers{' '}
                      <Tooltip
                        title={'Current Receivers'}
                        placement="bottom-start"
                        classes={{
                          tooltip: classes.tooltip,
                        }}
                      >
                        <InfoOutlinedIcon className={classes.grade_icon} />
                      </Tooltip>
                    </label>
                  </div>
                )}
              </div>

              {Array.from(userSettings.values())
                .sort(sortByAlertTypeAsc)
                .map((value) => {
                  return (
                    <>
                      <AlertType
                        isPrimaryContact={isPrimaryContact}
                        alertType={value.alertType}
                        tooltip={value.alertType}
                        onFrequencyChange={onFrequencyChange}
                        onEmailChange={onEmailChange}
                        onSmsChange={onSmsChange}
                        data={value}
                        isFetching={isFetching}
                        isRefetchError={isRefetchError}
                        status={receiversStatus}
                        alertTypeFrequencyOptions={
                          alertTypeFrequencyOptions[value.alertType]
                        }
                      />
                      <hr className={classes.separator} />
                    </>
                  )
                })}
            </Fragment>
          )
        }}
      />
    )
  }
)

export default AlertSettings
